/**
 * <cm-product-form>
 *
 * Custom AJAX add-to-cart wrapper. Wraps <product-form-component> and
 * intercepts the form submit in the CAPTURE phase, BEFORE the theme's
 * default handler can run. The default flow is replaced with a clean
 * /cart/add.js request that:
 *
 *   - never reloads the page
 *   - surfaces server errors (422 etc.) into the form's inline error host
 *   - refreshes /cart.js for an authoritative item_count
 *   - dispatches a single `cart:update` event with `data.source` set to
 *     'cm-product-form' so listeners that filter for the stock theme
 *     source ('product-form-component') — most importantly the
 *     cm-bundle-picker chain — stay silent. The cart drawer, cart icon,
 *     header live region and cart-items section all listen by event
 *     name and update normally.
 *
 * The existing <product-form-component> is left in place so its variant
 * morphing, quantity-rule UI, accelerated-checkout state etc. continue
 * to work — only the submit handler is taken over.
 *
 * Usage (in blocks/buy-buttons.liquid or any other product form host):
 *
 *   <cm-product-form data-product-id="{{ product.id }}">
 *     <product-form-component ...>
 *       <form ...>...</form>
 *     </product-form-component>
 *   </cm-product-form>
 */

const SOURCE_TAG = 'cm-product-form';
const ERROR_DISPLAY_MS = 10000;
const ADDED_RESET_MS = 1000;

class CmProductForm extends HTMLElement {
  constructor() {
    super();
    this._submitting = false;
    this._errorTimeout = null;
    this._onSubmitCapture = this._onSubmitCapture.bind(this);
    this._onVariantUpdate = this._onVariantUpdate.bind(this);
  }

  connectedCallback() {
    this.addEventListener('submit', this._onSubmitCapture, { capture: true });

    // CRITICAL: the theme's @theme/component framework registers a single
    // delegated capture-phase submit listener on `document` and dispatches
    // to whichever Component the closest `[on:submit]` attribute resolves
    // to. Because `document` is higher in the capture path than this
    // element, that delegated listener fires BEFORE our own — and would
    // run product-form-component.handleSubmit, performing a duplicate
    // /cart/add.js before our handler ever gets a chance to call
    // stopImmediatePropagation. Stripping the attribute makes the
    // framework's resolver return null and skip the call entirely
    // (component.js: getElement uses closest('[on\\:submit]'), then
    // element.getAttribute('on:submit') is read at event time).
    this._neutralizeInnerProductForm();

    // Re-apply if the inner element gets morphed during a variant change
    // and the attribute is re-added.
    this._attrObserver = new MutationObserver(() => this._neutralizeInnerProductForm());
    this._attrObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['on:submit'],
    });

    // Track pre-order state across variant changes. The inner hidden input
    // `[data-cm-preorder-property]` and the add-to-cart button label are
    // re-synced here — the form's hidden inputs are not part of any morph
    // target, so we must toggle them ourselves.
    this._section = this.closest('.shopify-section') || document;
    this._section.addEventListener('variant:update', this._onVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this._onSubmitCapture, { capture: true });
    if (this._section) {
      this._section.removeEventListener('variant:update', this._onVariantUpdate);
      this._section = null;
    }
    if (this._attrObserver) {
      this._attrObserver.disconnect();
      this._attrObserver = null;
    }
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
      this._errorTimeout = null;
    }
  }

  _neutralizeInnerProductForm() {
    this.querySelectorAll('product-form-component[on\\:submit]').forEach((el) => {
      el.removeAttribute('on:submit');
    });
  }

  /**
   * Pre-order = inventory managed by Shopify, on-hand quantity <= 0, but
   * the policy allows overselling. The variant is `available: true` but
   * physically empty.
   * @param {{ inventory_management?: string|null, inventory_quantity?: number|null, inventory_policy?: string|null } | null | undefined} variant
   * @returns {boolean}
   */
  _isVariantPreorder(variant) {
    if (!variant) return false;
    return (
      variant.inventory_management === 'shopify' &&
      typeof variant.inventory_quantity === 'number' &&
      variant.inventory_quantity <= 0 &&
      variant.inventory_policy === 'continue'
    );
  }

  /** @param {CustomEvent} event */
  _onVariantUpdate(event) {
    const variant = event.detail?.resource;
    if (!variant) return;

    // Only react to events for the product this form belongs to.
    const formProductId = this.dataset.productId;
    const eventProductId = event.detail?.data?.productId;
    if (formProductId && eventProductId && String(formProductId) !== String(eventProductId)) {
      return;
    }

    const preorderEnabled = this.dataset.preorderEnabled === 'true';
    const isPreorder = preorderEnabled && this._isVariantPreorder(variant);

    const propertyInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[data-cm-preorder-property]')
    );
    if (propertyInput) {
      propertyInput.disabled = !isPreorder;
    }
  }

  /** @param {SubmitEvent} event */
  async _onSubmitCapture(event) {
    const form = /** @type {HTMLFormElement | null} */ (event.target);
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.type && form.dataset.type !== 'add-to-cart-form') return;

    // Cancel native submit and silence every other submit listener up
    // and down the tree, including <product-form-component>'s
    // /handleSubmit.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this._submitting) return;
    this._submitting = true;

    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    const variantInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));

    const variantId = variantInput?.value?.trim() || '';
    const quantity =
      Number(quantityInput?.value) ||
      Number(this.dataset.quantityDefault) ||
      1;

    if (!variantId) {
      console.warn('[cm-product-form] No variant id on form; aborting');
      this._submitting = false;
      return;
    }

    this._setButtonState(submitter, 'loading');
    this._hideError(form);

    const sectionIds = this._collectSectionIds();
    const formData = new FormData(form);
    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));
    }

    /** @type {any} */
    let result;
    try {
      const response = await fetch(this._cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      result = await response.json();
    } catch (error) {
      console.error('[cm-product-form] Network error during /cart/add.js:', error);
      this._showError(form, this._fallbackErrorMessage());
      this._setButtonState(submitter, 'idle');
      this._submitting = false;
      return;
    }

    // Shopify's /cart/add.js puts a status field on the response only on
    // error (422, 404 …). On success the response is the added line item
    // and there's no status field.
    if (result && typeof result.status === 'number') {
      const message = result.message || result.description || this._fallbackErrorMessage();
      this._showError(form, message);
      this._dispatchError(result, variantId);
      this._setButtonState(submitter, 'idle');
      this._submitting = false;
      return;
    }

    // Success path — pull the authoritative cart count for cart-icon and
    // header live region. If /cart.js fails we fall back to scraping the
    // count out of the rendered cart-items section that came back inline.
    const cart = await this._fetchCart();
    let totalItemCount = (cart && typeof cart.item_count === 'number') ? cart.item_count : null;
    if (totalItemCount == null) {
      totalItemCount = this._scrapeCartItemCount(result?.sections, sectionIds);
    }
    if (totalItemCount == null) {
      totalItemCount = quantity;
    }

    this._setButtonState(submitter, 'added');
    this._announceAdded(form, submitter);

    const updateEvent = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart || undefined,
        sourceId: this.id || SOURCE_TAG,
        data: {
          source: SOURCE_TAG,
          itemCount: totalItemCount,
          variantId,
          productId: this.dataset.productId || form.dataset.productId,
          sections: result?.sections,
        },
      },
    });
    this.dispatchEvent(updateEvent);

    setTimeout(() => {
      this._setButtonState(submitter, 'idle');
    }, ADDED_RESET_MS);

    this._submitting = false;
  }

  /* ----------------------------- helpers ----------------------------- */

  _cartAddUrl() {
    return (
      (typeof window !== 'undefined' &&
        window.Theme &&
        window.Theme.routes &&
        window.Theme.routes.cart_add_url) ||
      '/cart/add.js'
    );
  }

  /** @returns {string[]} */
  _collectSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) {
        ids.push(node.dataset.sectionId);
      }
    });
    return ids;
  }

  async _fetchCart() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return await response.json();
    } catch (error) {
      console.warn('[cm-product-form] Could not refresh /cart.js:', error);
      return null;
    }
  }

  /**
   * Pull `[ref="cartItemCount"]` out of the rendered cart-items section
   * that comes back with /cart/add.js when `sections=...` is sent.
   * @param {Record<string, string> | undefined} sections
   * @param {string[]} sectionIds
   * @returns {number | null}
   */
  _scrapeCartItemCount(sections, sectionIds) {
    if (!sections) return null;
    for (const sectionId of sectionIds) {
      const html = sections[sectionId];
      if (typeof html !== 'string' || !html) continue;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const text = parsed.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
      const count = text ? parseInt(text, 10) : NaN;
      if (Number.isFinite(count)) return count;
    }
    return null;
  }

  /**
   * @param {HTMLElement | null} button
   * @param {'idle' | 'loading' | 'added'} state
   */
  _setButtonState(button, state) {
    if (!button) return;
    if (state === 'loading') {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('data-loading', 'true');
      // Don't disable — the button's own animation logic depends on a
      // click-able element. Visual loading state can be done with CSS
      // on [data-loading].
    } else if (state === 'added') {
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-busy');
      button.setAttribute('data-added', 'true');
    } else {
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-busy');
      button.removeAttribute('data-added');
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {string} message
   */
  _showError(form, message) {
    const errorHost = /** @type {HTMLElement | null} */ (
      form.querySelector('[ref="addToCartTextError"]') ||
      this.querySelector('[ref="addToCartTextError"]')
    );
    if (!errorHost) {
      console.warn('[cm-product-form] Add-to-cart error (no UI host found):', message);
      return;
    }
    errorHost.classList.remove('hidden');

    // The error host already contains an icon wrapper followed by a
    // text node — mirror product-form-component's behaviour so we don't
    // duplicate the icon.
    const textNode = errorHost.childNodes[2];
    if (textNode && textNode.nodeType === Node.TEXT_NODE) {
      textNode.textContent = message;
    } else {
      errorHost.appendChild(document.createTextNode(message));
    }
    errorHost.setAttribute('aria-live', 'polite');

    if (this._errorTimeout) clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => {
      errorHost.classList.add('hidden');
    }, ERROR_DISPLAY_MS);
  }

  /** @param {HTMLFormElement} form */
  _hideError(form) {
    const errorHost = /** @type {HTMLElement | null} */ (
      form.querySelector('[ref="addToCartTextError"]') ||
      this.querySelector('[ref="addToCartTextError"]')
    );
    if (errorHost) errorHost.classList.add('hidden');
    if (this._errorTimeout) {
      clearTimeout(this._errorTimeout);
      this._errorTimeout = null;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLElement | null} button
   */
  _announceAdded(form, button) {
    const liveRegion = /** @type {HTMLElement | null} */ (
      this.querySelector('[ref="liveRegion"]') ||
      form.querySelector('[ref="liveRegion"]')
    );
    if (!liveRegion) return;

    const addedText =
      button?.querySelector('.add-to-cart-text--added')?.textContent?.trim() ||
      (window.Theme && window.Theme.translations && window.Theme.translations.added) ||
      'Added';
    liveRegion.textContent = addedText;

    setTimeout(() => {
      if (liveRegion.textContent === addedText) liveRegion.textContent = '';
    }, 5000);
  }

  /**
   * @param {any} result
   * @param {string} variantId
   */
  _dispatchError(result, variantId) {
    const event = new CustomEvent('cart:error', {
      bubbles: true,
      detail: {
        sourceId: variantId,
        message: result?.message,
        description: result?.description,
        errors: result?.errors,
      },
    });
    this.dispatchEvent(event);
  }

  _fallbackErrorMessage() {
    return (
      (window.Theme && window.Theme.translations && window.Theme.translations.cart_error) ||
      'Could not add to cart. Please try again.'
    );
  }
}

if (!customElements.get('cm-product-form')) {
  customElements.define('cm-product-form', CmProductForm);
}
